import * as React from 'react'
import { bind } from 'classnames/bind'
import * as styles from '@procore/core-css/src/components/_modal.scss'

import addSubcomponents from '../utils/addSubcomponents'
import { PlacementNS, Props } from '../utils/types'
import { Visibility } from '../state/Visibility'
import BaseIcon from './Icon'
import Card from './Card'
import Portal from './Portal'

const cx = bind(styles)

type ModalPlacement = typeof PlacementNS.top | typeof PlacementNS.center

export namespace ModalIconVariantNS {
  export const confirm = 'confirm'
}

type ModalIconVariant = typeof ModalIconVariantNS.confirm

interface ModalHeaderProps extends Props {
  onClose?: (event: React.MouseEvent<HTMLDivElement>) => void
}

interface ModalIconProps extends Props {
  variant?: ModalIconVariant
}

interface ModalProps extends Props {
  onClickOverlay?: (event: React.MouseEvent<HTMLDivElement>) => void
  open?: boolean
  placement?: ModalPlacement
}

interface ConfirmModalProps extends ModalProps {
  headline?: string
  onClose?: (event: React.MouseEvent) => void
}

export const Modal = React.forwardRef<HTMLDivElement, ModalProps>(
  (
    {
      children,
      className,
      onClickOverlay = () => {},
      open = false,
      placement = PlacementNS.center,
      style,
      ...props
    },
    ref
  ) => (
    <Portal>
      <div
        ref={ref}
        className={cx('core-modal', `core-modal--placement-${placement}`, {
          'core-modal--open': open,
        })}
      >
        <div className={cx('core-modal__overlay')} onClick={onClickOverlay} />
        <Card className={cx('core-modal__container')} style={style}>
          <div className={cx('core-modal__content', className)} {...props}>
            {children}
          </div>
        </Card>
      </div>
    </Portal>
  )
)

export const Body = React.forwardRef<HTMLDivElement, Props>(
  ({ children, className, ...props }, ref) => (
    <div ref={ref} className={cx('core-modal__body', className)} {...props}>
      {children}
    </div>
  )
)

export const Footer = React.forwardRef<HTMLElement, Props>(
  ({ children, className, ...props }, ref) => (
    <footer
      ref={ref}
      className={cx('core-modal__footer', className)}
      {...props}
    >
      {children}
    </footer>
  )
)

export const FooterButtons = React.forwardRef<HTMLDivElement, Props>(
  ({ children, className, ...props }, ref) => (
    <div
      ref={ref}
      className={cx('core-modal__footer__buttons', className)}
      {...props}
    >
      {children}
    </div>
  )
)

export const FooterNotation = React.forwardRef<HTMLDivElement, Props>(
  ({ children, className, ...props }, ref) => (
    <div
      ref={ref}
      className={cx('core-modal__footer__notation', className)}
      {...props}
    >
      {children}
    </div>
  )
)

export const Header = React.forwardRef<HTMLDivElement, ModalHeaderProps>(
  (
    {
      children,
      className,
      onClose = (event: React.MouseEvent<HTMLDivElement>) => {},
      ...props
    },
    ref
  ) => (
    <div
      ref={ref}
      className={`${cx('core-modal__header')} ${className} `}
      {...props}
    >
      <div
        className={`${cx('core-modal__dismiss')} ci ci-x`}
        onClick={onClose}
      />
      {children}
    </div>
  )
)

export const Icon = React.forwardRef<HTMLDivElement, ModalIconProps>(
  ({ variant }, ref) => (
    <div
      ref={ref}
      className={cx('core-modal__icon', {
        [`core-modal__icon--${variant}`]: variant,
      })}
    >
      <BaseIcon icon="warning-triangle" clickable={false} />
    </div>
  )
)

export const ConfirmModal = React.forwardRef<HTMLDivElement, ConfirmModalProps>(
  ({ children, headline, onClose, ...props }, ref) => (
    <Modal ref={ref} {...props}>
      <Header onClose={onClose}>
        <Icon variant={ModalIconVariantNS.confirm} />
        {headline}
      </Header>
      {children}
    </Modal>
  )
)

export const State = Visibility

Modal.displayName = 'Modal'

Body.displayName = 'Modal.Body'

Footer.displayName = 'Modal.Footer'

FooterButtons.displayName = 'Modal.FooterButtons'

FooterNotation.displayName = 'Modal.FooterNotation'

Header.displayName = 'Modal.Header'

Icon.displayName = 'Modal.Icon'

export default addSubcomponents(
  {
    Body,
    Footer,
    FooterButtons,
    FooterNotation,
    Header,
    Icon,
    State,
  },
  Modal
)
